import React, { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { Tooltip as UiTooltip } from '../../../../Tooltip';
import styles from './detailsPageChartCard.module.css';
import SimplePlusIcon from '../../../../../assets/SimplePlusIcon';

export interface LineConfig {
  key: string;
  name: string;
  color: string;
}

interface LineChartCardProps {
  title: string;
  data: Record<string, any>[];
  lines: LineConfig[];
  xAxisKey: string;
  yTickFormatter?: (value: number) => string;
  isLoading?: boolean;
  onMonthClick?: (monthLabel: string, visibleKeys: string[]) => void;
}

const DetailsPageChartCard: React.FC<LineChartCardProps> = ({
  title,
  data,
  lines,
  xAxisKey,
  yTickFormatter,
  isLoading,
  onMonthClick
}) => {
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(new Set());
  const toggleLine = (key: string) => {
    setHiddenKeys(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const visibleLines = lines.filter(l => !hiddenKeys.has(l.key));

  // console.log("DetailsPageChartCard", data);
  return (
    <div className={styles.card}>
      {/* Header */}
      <div className={styles.header}>
        <span className={styles.title}>{title}</span>
      </div>

      <div className={styles.divider} />

      {isLoading ? (
        <div className={styles.shimmerArea}>
          <div className={`${styles.shimmerBlock} ${styles.shimmerChart}`} />
          <div className={styles.shimmerLegendRow}>
            {[1, 2, 3].map(i => (
              <div key={i} className={`${styles.shimmerBlock} ${styles.shimmerPill}`} />
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* Chart */}
          <div className={styles.chartArea}>
            <ResponsiveContainer width="100%" height="100%">
              {/* <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}> */}
              <LineChart
                data={data}
                margin={{ top: 8, right: 12, left: 0, bottom: 4 }}
                style={onMonthClick ? { cursor: 'pointer' } : undefined}
                onClick={(chartData: any) => {
                  if (onMonthClick && chartData?.activeLabel) {
                    onMonthClick(String(chartData.activeLabel), visibleLines.map(l => l.key));
                  }
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#CBD5E1" />
                <XAxis
                  dataKey={xAxisKey}
                  // tick={{ fontSize: 10, fill: '#64748b' }}
                  axisLine={false}
                  tickLine={false}
                  tick={onMonthClick
                    ? (props: any) => {
                        const { x, y, payload } = props;
                        return (
                          <g transform={`translate(${x},${y})`} style={{ cursor: 'pointer' }} 
                          onClick={(e) => {
                              // Prevent bubbling to LineChart onClick to avoid duplicate month callbacks.
                            e.stopPropagation();
                            onMonthClick(payload.value, visibleLines.map(l => l.key))}
                          }>
                            <text x={0} y={0} dy={12} textAnchor="middle" fill="#0D6AB3" fontSize={10} fontWeight={700}>
                              {payload.value}
                            </text>
                          </g>
                        );
                      }
                    : { fontSize: 10, fill: '#64748b' }
                  }
                />
                <YAxis
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  axisLine={false}
                  tickLine={false}
                  width={45}
                  tickFormatter={yTickFormatter}
                />
                <Tooltip
                  contentStyle={{
                    fontSize: 10,
                    borderRadius: 4,
                    border: '1px solid #e2e8f0',
                  }}
                />
                {visibleLines.map((line) => (
                  <Line
                    key={line.key}
                    type="monotone"
                    dataKey={line.key}
                    name={line.name}
                    stroke={line.color}
                    strokeWidth={2}
                    dot={{ r: 3, fill: line.color, strokeWidth: 0 }}
                    activeDot={{ r: 5 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {onMonthClick && (
            <div style={{ padding: '0 var(--space-2sm) 2px', fontSize: 10, color: '#6f7a85' }}>
              Click a month to view daily breakdown
            </div>
          )}

          {/* Legend row */}
          <div className={styles.legend}>
            <div className={styles.legendItems}>
              {lines.map((line) => {
                const hidden = hiddenKeys.has(line.key);
                return (
                  <div key={line.key} className={styles.legendItem} style={{ opacity: hidden ? 0.4 : 1 }}>
                    <div className={styles.legendDot} style={{ backgroundColor: line.color }} />
                    <span className={styles.legendName} style={{ color: line.color }}>
                      {line.name}
                    </span>
                    <UiTooltip text={hidden ? 'Show' : 'Hide'} placement="top">
                    <button
                      className={styles.legendRemove}
                      onClick={() => toggleLine(line.key)}
                    >
                      {hidden ? (
                        <SimplePlusIcon color="var(--Primary-Color-Shade-1)" size={10} />
                      ) : (
                        <span style={{ display: 'inline-flex', transform: 'rotate(45deg)' }}>
                          <SimplePlusIcon color="var(--Primary-Color-Shade-1)" size={10} />
                        </span>
                      )}
                    </button>
                    </UiTooltip>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DetailsPageChartCard;
